/**
 * One number, its trend, and the ⓘ that says what it means.
 *
 * THE ⓘ IS NOT DECORATION. Every word of it — the formula, the grain, the
 * caveat — comes from `ListMetricDefinitions`, which serves
 * docs/kpi-catalogue.md. There is deliberately no Persian formula text in this
 * file: two definitions of «نرخ تبدیل» would eventually disagree, and the one
 * on the screen would be the one people believed.
 *
 * Three states this component exists to render honestly, because each of them
 * is a number that would otherwise be wrong:
 *
 *   «—»          the denominator was too small. "100% conversion" from one
 *                contact is worse than no number: it gets quoted.
 *   badge        the metric rests on a decision the stakeholders have not
 *                confirmed. Saying so on the SCREEN, not only in a document
 *                nobody opens twice.
 *   grey cell    an immature cohort. NOT a zero — plotting it as one invents
 *                a decline that is an artefact of the calendar.
 */
import { Badge, Card } from '@meshhub/design-tokens/ui';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Info, Minus } from 'lucide-react';
import React, { useState } from 'react';
import { formatCount, toPersianDigits } from '../../lib/format.js';
import { S } from '../../lib/strings.js';

/** A number, or «—» when the server said the sample was too small. */
export function formatMetricValue(value, { isRate, suppressed }) {
  if (suppressed || value === null || value === undefined) return '—';
  if (isRate) return `${toPersianDigits(Number(value).toFixed(1))}٪`;
  return formatCount(Math.round(Number(value) * 100) / 100);
}

function Delta({ value }) {
  if (value === null || value === undefined) {
    return (
      <span className="flex items-center gap-1 text-xs text-text-muted">
        <Minus size={12} aria-hidden="true" />
      </span>
    );
  }
  const up = value > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`flex items-center gap-1 text-xs ${up ? 'text-success' : 'text-danger'}`}
      // A delta with no baseline is meaningless, so the title always names
      // what it is measured against rather than leaving the reader to guess.
      title={S.dashboard.comparedTo}
    >
      <Icon size={12} aria-hidden="true" />
      {toPersianDigits(Math.abs(value).toFixed(1))}٪
    </span>
  );
}

/**
 * A sparkline as flex rows, like the BarChart — no charting library.
 *
 * NULLS ARE GAPS, not zeros. A cohort that has not matured must not be drawn
 * at the bottom of the chart beside one that converted nobody.
 */
function Sparkline({ values }) {
  const present = values.filter((v) => v !== null && v !== undefined);
  if (present.length < 2) return null;
  const max = Math.max(...present, 0);
  const min = Math.min(...present, 0);
  const span = max - min || 1;
  return (
    <div className="flex h-sparkline items-end gap-px" aria-hidden="true">
      {values.map((v, i) => {
        const missing = v === null || v === undefined;
        return (
          <div
            key={i}
            className={missing ? 'flex-1 bg-transparent' : 'flex-1 bg-primary/40'}
            // A PERCENTAGE, not a pixel: the bar's height is a share of the
            // sparkline's own token height, so changing that token rescales
            // every bar rather than leaving a hardcoded floor behind.
            //
            // A missing point is a GAP with a hairline, not a zero bar. An
            // immature cohort drawn at the bottom of the chart beside one that
            // converted nobody would say the two are the same thing.
            style={{ height: missing ? '2%' : `${Math.max(6, ((v - min) / span) * 100)}%` }}
          />
        );
      })}
    </div>
  );
}

export function MetricCard({ metric, error, onDrillDown }) {
  const [showInfo, setShowInfo] = useState(false);

  if (error) {
    // ONE broken card beside nineteen working ones. The dashboard renders the
    // message in place of the number rather than failing the page.
    return (
      <Card className="flex flex-col gap-2 p-4">
        <div className="text-sm font-medium text-text">{error.name}</div>
        <div className="flex items-start gap-2 text-xs text-danger">
          <AlertTriangle size={14} className="mt-px shrink-0" aria-hidden="true" />
          <span>
            {S.dashboard.metricFailed}: {error.message}
          </span>
        </div>
      </Card>
    );
  }

  const { definition: d } = metric;
  const primary = metric.rows.length === 1 ? metric.rows[0] : null;
  const series = primary?.values ?? [];

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-text">{d.titleFa}</h3>
        <button
          type="button"
          onClick={() => setShowInfo((v) => !v)}
          aria-expanded={showInfo}
          aria-label={`${S.dashboard.definition}: ${d.titleFa}`}
          className="shrink-0 rounded p-1 text-text-muted hover:bg-surface-hover hover:text-text"
        >
          <Info size={14} aria-hidden="true" />
        </button>
      </div>

      <div className="flex items-end justify-between gap-2">
        <span className="text-2xl font-bold tabular-nums text-text">
          {formatMetricValue(metric.total, {
            isRate: d.isRate,
            suppressed: metric.suppressed,
          })}
        </span>
        <Delta value={metric.deltaPct} />
      </div>

      {series.length > 1 && <Sparkline values={series} />}

      {metric.suppressed && (
        <p className="text-xs text-text-muted" title={S.dashboard.suppressedHint}>
          {S.dashboard.suppressed}
          {metric.denominator > 0 && ` (${formatCount(metric.denominator)})`}
        </p>
      )}

      {d.needsConfirmation && (
        <Badge
          className="w-fit bg-warning-subtle text-warning"
          title={S.dashboard.needsConfirmationHint}
        >
          {S.dashboard.needsConfirmation}
        </Badge>
      )}

      {onDrillDown && (
        <button
          type="button"
          onClick={() => onDrillDown(metric, primary?.key ?? '')}
          className="w-fit text-xs text-primary hover:underline"
        >
          {S.dashboard.drillDown}
        </button>
      )}

      {showInfo && (
        <dl className="flex flex-col gap-2 border-t border-border pt-2 text-xs">
          <div>
            <dt className="font-medium text-text">{S.dashboard.formula}</dt>
            <dd className="text-text-muted">{d.formula}</dd>
          </div>
          <div>
            <dt className="font-medium text-text">{S.dashboard.grain}</dt>
            <dd className="text-text-muted">{d.grain}</dd>
          </div>
          {d.caveat && (
            <div>
              {/* The caveat is rendered in the SAME weight as the formula.
                  A caveat in small grey text is a caveat nobody reads. */}
              <dt className="font-medium text-warning">{S.dashboard.caveat}</dt>
              <dd className="text-text">{d.caveat}</dd>
            </div>
          )}
          <div>
            <dt className="font-medium text-text">{S.dashboard.sources}</dt>
            <dd className="text-text-muted" dir="ltr">
              {d.sources.join('، ')}
            </dd>
          </div>
        </dl>
      )}
    </Card>
  );
}
